import { type ReactNode } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";

type Variant = "default" | "danger";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void | Promise<void>;
  /**
   * Disables both buttons while a parent-owned async action is running.
   * The parent is responsible for toggling this around its async work.
   */
  loading?: boolean;
  /** "danger" turns the confirm button red — use for destructive actions. */
  variant?: Variant;
};

export default function ConfirmDrawer({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "تایید",
  cancelLabel = "انصراف",
  onConfirm,
  loading,
  variant = "default",
}: Props) {
  const confirmClass =
    variant === "danger"
      ? "bg-danger hover:bg-danger/90"
      : "bg-primary hover:bg-primary/90";

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>{title}</DrawerTitle>
          {description ? (
            <DrawerDescription>{description}</DrawerDescription>
          ) : null}
        </DrawerHeader>

        <DrawerFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="flex-1 h-11 rounded-lg border border-default-100 bg-background text-sm font-semibold text-default-700 disabled:opacity-60"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => void onConfirm()}
            disabled={loading}
            className={`flex-1 h-11 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60 ${confirmClass}`}
          >
            {loading ? "در حال انجام..." : confirmLabel}
          </button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
